import { Controller } from "@hotwired/stimulus"

/**
 * Base controller for video URL processing and utilities
 * 
 * USAGE PATTERNS:
 * 
 * 1. EXTEND FOR CUSTOM FUNCTIONALITY:
 *    import VideoBaseController from "./video_base_controller"
 *    export default class extends VideoBaseController { ... }
 * 
 * 2. DIRECT USAGE FOR UTILITIES:
 *    data-controller="video-base" (though typically you'd extend it)
 * 
 * 3. EXAMPLE EXTENSIONS:
 *    - VideoPreviewController: Shows embedded video previews in forms
 *    - VideoUrlValidatorController: Just validates URLs without preview
 *    - VideoEmbedController: Generate embed codes for display
 *    - VideoThumbnailController: Extract thumbnails from video URLs
 * 
 * CORE METHODS AVAILABLE:
 * - getEmbedUrl(url): Returns embeddable iframe URL
 * - isValidVideoUrl(url): Boolean validation
 * - getPlatform(url): Returns 'youtube', 'vimeo', or 'unknown'
 * - validateVideoUrl(url): Returns {valid, error/url}
 * - generateEmbedHtml(embedUrl, options): Creates iframe HTML
 * - generateExternalLink(url, platform): Creates external link HTML
 */
export default class extends Controller {
  
  // Core video URL processing methods
  getEmbedUrl(url) {
    if (!url || typeof url !== 'string') return null
    
    // YouTube URLs
    if (this.isYouTubeUrl(url)) {
      const videoId = this.extractYouTubeVideoId(url)
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null
    }
    
    // Vimeo URLs
    if (this.isVimeoUrl(url)) {
      const videoId = this.extractVimeoVideoId(url)
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null
    }
    
    return null
  }

  isValidVideoUrl(url) {
    return this.getEmbedUrl(url) !== null
  }

  getPlatform(url) {
    if (this.isYouTubeUrl(url)) return 'youtube'
    if (this.isVimeoUrl(url)) return 'vimeo'
    return 'unknown'
  }

  getVideoTitle(url) {
    switch(this.getPlatform(url)) {
      case 'youtube': return 'YouTube Video'
      case 'vimeo': return 'Vimeo Video'
      default: return 'Video'
    }
  }

  getPlatformColor(platform) {
    const colors = {
      'youtube': 'danger',
      'vimeo': 'info',
      'unknown': 'secondary'
    }
    return colors[platform] || 'secondary'
  }

  // YouTube URL detection and processing
  isYouTubeUrl(url) {
    return url.includes('youtube.com') || url.includes('youtu.be')
  }

  extractYouTubeVideoId(url) {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/
    ]
    
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match && match[1]) {
        return match[1]
      }
    }
    return null
  }

  // Vimeo URL detection and processing
  isVimeoUrl(url) {
    return url.includes('vimeo.com')
  }

  extractVimeoVideoId(url) {
    const pattern = /vimeo\.com\/(?:.*\/)?(\d+)/
    const match = url.match(pattern)
    return match && match[1] ? match[1] : null
  }

  // Utility methods for generating HTML
  generateEmbedHtml(embedUrl, options = {}) {
    const {
      width = '100%',
      height = '300',
      allowFullscreen = true,
      frameBorder = '0'
    } = options

    const allowAttrs = [
      'accelerometer', 'autoplay', 'clipboard-write', 
      'encrypted-media', 'gyroscope', 'picture-in-picture'
    ].join('; ')

    return `
      <iframe 
        src="${embedUrl}" 
        width="${width}" 
        height="${height}" 
        frameborder="${frameBorder}" 
        allow="${allowAttrs}" 
        class="rounded-3"
        ${allowFullscreen ? 'allowfullscreen' : ''}>
      </iframe>
    `
  }

  // Utility for creating external link HTML
  generateExternalLink(originalUrl, platform) {
    return `
      <a href="${originalUrl}" target="_blank" class="video-external-link">
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" class="me-1">
          <path d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"/>
          <path d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"/>
        </svg>
        View on ${platform.toUpperCase()}
      </a>
    `
  }

  // URL cleanup utility
  cleanVideoUrl(url) {
    if (!url) return ''
    return url.trim()
  }

  // Validation with user-friendly error messages
  validateVideoUrl(url) {
    const cleanUrl = this.cleanVideoUrl(url)
    
    if (!cleanUrl) {
      return { valid: false, error: 'Please enter a video URL' }
    }

    if (!this.isValidVideoUrl(cleanUrl)) {
      return { 
        valid: false, 
        error: 'Please enter a valid YouTube or Vimeo URL' 
      }
    }

    return { valid: true, url: cleanUrl }
  }
}